import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Toast from './Toast';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import PanelBreadcrumbs from './PanelBreadcrumbs';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PinnedReports = ({ setActivePage }) => {
  const [pinnedReports, setPinnedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchPinnedReports();
  }, []);

  // Check if we need to auto-view a pinned report (from Chat page)
  useEffect(() => {
    const viewPinnedReportId = sessionStorage.getItem('viewPinnedReportId');
    if (viewPinnedReportId && pinnedReports.length > 0) {
      const report = pinnedReports.find(r => r.id === parseInt(viewPinnedReportId));
      if (report) {
        sessionStorage.removeItem('viewPinnedReportId');
        // Auto-view the pinned report
        viewPinnedReport(report);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedReports]);

  const fetchPinnedReports = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      const response = await axios.get(`${API_BASE_URL}/api/pinned-reports`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const reports = response.data.pinned_reports || response.data.data || response.data || [];
      
      const safeJsonParse = (value) => {
        if (value && typeof value === 'string') {
          try {
            return JSON.parse(value);
          } catch (e) {
            return null;
          }
        }
        return value;
      };

      // Parse visualization/data (prefer list API's `visualization_config` + `saved_data` for card previews)
      const parsedReports = reports.map(report => {
        // New list response:
        // - visualization_config: { type, x_axis, y_axis, title, ... }
        // - saved_data: [ { ...row }, ... ]
        let visualization =
          safeJsonParse(report.visualization_config) ??
          safeJsonParse(report.visualizationConfig) ??
          safeJsonParse(report.visualization) ??
          null;

        let data =
          safeJsonParse(report.saved_data) ??
          safeJsonParse(report.savedData) ??
          safeJsonParse(report.data) ??
          [];

        if (!Array.isArray(data)) {
          // If backend ever nests it as { data: [...] }, handle gracefully
          data = Array.isArray(data?.data) ? data.data : [];
        }
        
        return {
          ...report,
          visualization,
          data
        };
      });
      
      setPinnedReports(parsedReports);
    } catch (error) {
      if (error.isSessionExpired || (error.response && error.response.status === 401)) {
        return;
      }
      console.error('Error fetching pinned reports:', error);
      setError('Failed to load pinned reports');
    } finally {
      setLoading(false);
    }
  };

  const getVisualizationIcon = (visualizationType) => {
    if (!visualizationType) return '📊';
    const type = visualizationType.toLowerCase();
    if (type === 'line' || type === 'group_line') return '📈';
    if (type === 'bar' || type === 'group_bar') return '📊';
    if (type === 'pie' || type === 'donut') return '🥧';
    if (type === 'table') return '📋';
    return '📊';
  };

  const formatHumanReadableDate = (dateValue) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return String(dateValue || '');
    }

    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    return `${day} ${month}, ${year}`;
  };

  const isDateLikeValue = (value) => {
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime());
    }

    if (typeof value !== 'string') {
      return false;
    }

    const trimmed = value.trim();
    if (!trimmed) return false;

    const looksLikeDate =
      /\d{4}-\d{2}-\d{2}/.test(trimmed) ||
      trimmed.includes('T') ||
      /\d{1,2} [A-Za-z]{3}, \d{4}/.test(trimmed) ||
      /\d{1,2}\/\d{1,2}\/\d{4}/.test(trimmed);

    if (!looksLikeDate) return false;

    const parsed = new Date(trimmed);
    return !Number.isNaN(parsed.getTime());
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return formatHumanReadableDate(dateString);
    } catch (e) {
      return dateString;
    }
  };

  const formatTableCellValue = (value, key = '') => {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'number') {
      return value.toLocaleString('en-IN', { maximumFractionDigits: 0 });
    }

    const keyLooksDateLike = /date|time|created_at|updated_at|timestamp/i.test(String(key));
    if ((keyLooksDateLike || isDateLikeValue(value)) && isDateLikeValue(value)) {
      return formatHumanReadableDate(value);
    }

    return String(value);
  };

  const formatChartLabel = (label) => {
    if (isDateLikeValue(label)) {
      return formatHumanReadableDate(label);
    }

    return String(label);
  };

  const viewPinnedReport = async (report) => {
    try {
      setLoadingReport(true);
      setSelectedReport(report);
      
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      // Fetch latest data based on SQL query and visualization info
      const response = await axios.get(
        `${API_BASE_URL}/api/pinned-reports/${report.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Handle nested response structure: { success: true, data: { ... } }
      const fetchedData = response.data.data || response.data;
      console.log('Fetched report data:', fetchedData);
      
      // Parse visualization if it's a string
      let visualization = fetchedData.visualization || null;
      if (visualization && typeof visualization === 'string') {
        try {
          visualization = JSON.parse(visualization);
        } catch (e) {
          console.error('Error parsing visualization:', e);
          visualization = null;
        }
      }
      
      // Ensure dataTable is an array - check nested structure
      let dataTable = fetchedData.data || [];
      if (!Array.isArray(dataTable)) {
        dataTable = [];
      }
      
      const reportDataToSet = {
        content: fetchedData.description || fetchedData.message || null,
        dataTable: dataTable,
        summary: fetchedData.summary || null,
        visualization: visualization,
        csvFileName: fetchedData.csv_file_name || null,
        sqlQuery: fetchedData.sql_query || null,
        chatMessageId: fetchedData.chat_message_id || report.chat_message_id || null,
      };
      
      console.log('Setting report data:', reportDataToSet);
      setReportData(reportDataToSet);
    } catch (error) {
      if (error.isSessionExpired || (error.response && error.response.status === 401)) {
        return;
      }
      console.error('Error viewing pinned report:', error);
      setToast({ message: error.response?.data?.error || 'Failed to load pinned report', type: 'error' });
      setSelectedReport(null);
    } finally {
      setLoadingReport(false);
    }
  };

  const closeModal = () => {
    setSelectedReport(null);
    setReportData(null);
  };

  const downloadCSV = async (report) => {
    try {
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;
      
      const response = await axios.get(
        `${API_BASE_URL}/api/pinned-reports/${report.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Handle nested response structure: { success: true, data: { ... } }
      const reportData = response.data.data || response.data;
      
      const dataArray = reportData.data || [];
      if (Array.isArray(dataArray) && dataArray.length > 0) {
        // Convert to CSV
        const keys = Object.keys(dataArray[0]);
        const orderedHeaders = [];
        if (reportData.visualization?.x_axis && keys.includes(reportData.visualization.x_axis)) {
          orderedHeaders.push(reportData.visualization.x_axis);
        }
        keys.forEach(key => {
          if (!orderedHeaders.includes(key)) {
            orderedHeaders.push(key);
          }
        });
        
        const headerRow = orderedHeaders.map(header => 
          `"${header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}"`
        ).join(',');
        
        const dataRows = dataArray.map(row => {
          return orderedHeaders.map(header => {
            const value = row[header];
            const stringValue = value === null || value === undefined ? '' : String(value);
            return `"${stringValue.replace(/"/g, '""')}"`;
          }).join(',');
        });
        
        const csvContent = [headerRow, ...dataRows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${reportData.csv_file_name || report.csv_file_name || 'report_' + new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      if (error.isSessionExpired || (error.response && error.response.status === 401)) {
        return;
      }
      console.error('Error downloading CSV:', error);
      setToast({ message: 'Failed to download CSV', type: 'error' });
    }
  };

  const prepareChartData = (visualization, data) => {
    if (!visualization || !data || !Array.isArray(data) || data.length === 0) {
      return null;
    }

    const { type, x_axis, y_axis, title, series, y_axis_left, y_axis_right } = visualization;

    const coerceNumber = (val) => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const cleaned = val.replace(/,/g, '').trim();
        const n = Number(cleaned);
        return Number.isFinite(n) ? n : 0;
      }
      return 0;
    };

    // Resolve a series key even if backend returns different casing / spacing
    const resolveKey = (row, desiredKey) => {
      if (!row || !desiredKey) return desiredKey;
      if (Object.prototype.hasOwnProperty.call(row, desiredKey)) return desiredKey;

      const keys = Object.keys(row);
      const normalize = (s) =>
        String(s)
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '_')
          .replace(/[^a-z0-9_]/g, '');
      const desiredNorm = normalize(desiredKey);

      // 1) Exact normalized match
      const exact = keys.find((k) => normalize(k) === desiredNorm);
      if (exact) return exact;

      // 2) Match ignoring underscores entirely (e.g. totalSales vs total_sales)
      const stripUnderscore = (s) => normalize(s).replace(/_/g, '');
      const desiredNoUnderscore = stripUnderscore(desiredKey);
      const underscoreMatch = keys.find((k) => stripUnderscore(k) === desiredNoUnderscore);
      if (underscoreMatch) return underscoreMatch;

      // 3) Contains match (last resort) - helps when series label is a shorter form
      const contains = keys.find((k) => stripUnderscore(k).includes(desiredNoUnderscore));
      return contains || desiredKey;
    };

    // Dual-axis line chart: two metrics with separate y-axes
    if (type === 'dual_axis_line') {
      const xKey = resolveKey(data[0], x_axis);
      const leftKey = resolveKey(data[0], y_axis_left);
      const rightKey = resolveKey(data[0], y_axis_right);

      const labels = data.map((item) => formatChartLabel(item?.[xKey]));
      const leftValues = data.map((item) => coerceNumber(item?.[leftKey]));
      const rightValues = data.map((item) => coerceNumber(item?.[rightKey]));

      return {
        labels,
        datasets: [
          {
            label: String(y_axis_left || 'Left Axis'),
            data: leftValues,
            borderColor: 'rgba(99, 102, 241, 1)',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            yAxisID: 'yLeft',
          },
          {
            label: String(y_axis_right || 'Right Axis'),
            data: rightValues,
            borderColor: 'rgba(34, 197, 94, 1)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            yAxisID: 'yRight',
          },
        ],
      };
    }
    
    if (series && Array.isArray(series) && series.length > 0) {
      const xKey = resolveKey(data[0], x_axis);
      const labels = data.map(item => formatChartLabel(item?.[xKey]));
      
      const backgroundColors = [
        'rgba(99, 102, 241, 0.7)',
        'rgba(139, 92, 246, 0.7)',
        'rgba(236, 72, 153, 0.7)',
        'rgba(251, 146, 60, 0.7)',
        'rgba(34, 197, 94, 0.7)',
        'rgba(59, 130, 246, 0.7)',
        'rgba(168, 85, 247, 0.7)',
        'rgba(245, 101, 101, 0.7)',
      ];
      
      const datasets = series.map((seriesName, index) => {
        const values = data.map(item => {
          const key = resolveKey(item, seriesName);
          return coerceNumber(item?.[key]);
        });
        
        return {
          label: String(seriesName),
          data: values,
          backgroundColor: (type === 'line' || type === 'group_line')
            ? backgroundColors[index % backgroundColors.length].replace('0.7', '0.1')
            : backgroundColors[index % backgroundColors.length],
          borderColor: backgroundColors[index % backgroundColors.length].replace('0.7', '1'),
          borderWidth: 2,
          tension: (type === 'line' || type === 'group_line') ? 0.4 : 0,
        };
      });
      
      return { labels, datasets };
    }
    
    if (type === 'group_line') {
      const xKey = resolveKey(data[0], x_axis);
      const yKey = resolveKey(data[0], y_axis);
      const rawXValues = [...new Set(
        data
          .map((item) => item?.[xKey])
          .filter((value) => value !== undefined && value !== null)
      )];
      const labels = rawXValues.map((value) => formatChartLabel(value));
      const allKeys = Object.keys(data[0] || {});
      let categoryKey = (typeof series === 'string' && series.trim()) ? resolveKey(data[0], series.trim()) : null;
      if (!categoryKey || !allKeys.includes(categoryKey)) {
        categoryKey = allKeys.find(key =>
          key.toLowerCase() === 'category' ||
          key.toLowerCase() === 'group'
        );
      }
      if (!categoryKey) {
        categoryKey = allKeys.find(key =>
          key !== xKey &&
          key !== yKey
        );
      }
      
      const categories = categoryKey 
        ? [...new Set(data.map(item => item[categoryKey]))]
        : [];
      
      if (!categoryKey || categories.length === 0) {
        const values = rawXValues.map((rawX) => {
          const matchingRows = data.filter((row) => String(row?.[xKey]) === String(rawX));
          if (!matchingRows.length) return 0;
          return matchingRows.reduce((sum, row) => sum + coerceNumber(row?.[yKey]), 0);
        });
        
        return {
          labels,
          datasets: [{
            label: y_axis.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            data: values,
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderColor: 'rgba(99, 102, 241, 1)',
            borderWidth: 2,
            tension: 0.4,
          }],
        };
      }
      
      const backgroundColors = [
        'rgba(99, 102, 241, 0.7)',
        'rgba(139, 92, 246, 0.7)',
        'rgba(236, 72, 153, 0.7)',
        'rgba(251, 146, 60, 0.7)',
        'rgba(34, 197, 94, 0.7)',
        'rgba(59, 130, 246, 0.7)',
        'rgba(168, 85, 247, 0.7)',
        'rgba(245, 101, 101, 0.7)',
      ];
      
      const datasets = categories.map((category, index) => {
        const values = rawXValues.map((rawX) => {
          const matchingRows = data.filter((row) =>
            String(row?.[xKey]) === String(rawX) &&
            String(row?.[categoryKey]) === String(category)
          );
          if (!matchingRows.length) return 0;
          return matchingRows.reduce((sum, row) => sum + coerceNumber(row?.[yKey]), 0);
        });
        
        return {
          label: String(category),
          data: values,
          backgroundColor: backgroundColors[index % backgroundColors.length].replace('0.7', '0.1'),
          borderColor: backgroundColors[index % backgroundColors.length].replace('0.7', '1'),
          borderWidth: 2,
          tension: 0.4,
        };
      });
      
      return { labels, datasets };
    } else if (type === 'line' || type === 'bar') {
      const xKey = resolveKey(data[0], x_axis);
      const yKey = resolveKey(data[0], y_axis);
      const labels = data.map(item => formatChartLabel(item?.[xKey]));
      const values = data.map(item => {
        return coerceNumber(item?.[yKey]);
      });
      
      return {
        labels,
        datasets: [{
          label: y_axis.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          data: values,
          backgroundColor: type === 'bar' 
            ? 'rgba(99, 102, 241, 0.5)'
            : 'rgba(99, 102, 241, 0.1)',
          borderColor: 'rgba(99, 102, 241, 1)',
          borderWidth: 2,
          tension: type === 'line' ? 0.4 : 0,
        }],
      };
    } else if (type === 'pie' || type === 'donut') {
      const labels = data.map(item => formatChartLabel(item[x_axis]));
      const values = data.map(item => item[y_axis]);
      
      const backgroundColors = [
        'rgba(99, 102, 241, 0.8)',
        'rgba(139, 92, 246, 0.8)',
        'rgba(236, 72, 153, 0.8)',
        'rgba(251, 146, 60, 0.8)',
        'rgba(34, 197, 94, 0.8)',
        'rgba(59, 130, 246, 0.8)',
      ];
      
      return {
        labels,
        datasets: [{
          label: y_axis.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          data: values,
          backgroundColor: backgroundColors.slice(0, values.length),
          borderColor: '#fff',
          borderWidth: 2,
        }],
      };
    }
    
    return null;
  };

  const renderChart = (visualization, data) => {
    const chartData = prepareChartData(visualization, data);
    if (!chartData) return null;

    const { type, title } = visualization;
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: !!title,
          text: title || '',
        },
      },
    };

    if (type === 'dual_axis_line') {
      const dualAxisOptions = {
        ...chartOptions,
        interaction: { mode: 'index', intersect: false },
        scales: {
          yLeft: {
            type: 'linear',
            position: 'left',
            grid: { drawOnChartArea: true },
          },
          yRight: {
            type: 'linear',
            position: 'right',
            grid: { drawOnChartArea: false },
          },
        },
      };
      return <Line data={chartData} options={dualAxisOptions} />;
    }

    if (type === 'line' || type === 'group_line') {
      return <Line data={chartData} options={chartOptions} />;
    } else if (type === 'bar' || type === 'group_bar') {
      return <Bar data={chartData} options={chartOptions} />;
    } else if (type === 'pie' || type === 'donut') {
      return <Pie data={chartData} options={chartOptions} />;
    }
    
    return null;
  };

  // Render thumbnail chart for card preview
  const renderThumbnailChart = (visualization, data) => {
    if (!visualization || !data || !Array.isArray(data) || data.length === 0) {
      return null;
    }

    // Parse visualization if it's a string
    let vis = visualization;
    if (typeof vis === 'string') {
      try {
        vis = JSON.parse(vis);
      } catch (e) {
        return null;
      }
    }

    const chartData = prepareChartData(vis, data);
    if (!chartData) return null;

    const { type } = vis;
    const thumbnailOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false, // Hide legend in thumbnail
        },
        title: {
          display: false, // Hide title in thumbnail
        },
        tooltip: {
          enabled: false, // Disable tooltips in thumbnail
        },
      },
      scales: type !== 'pie' && type !== 'donut' ? (
        type === 'dual_axis_line'
          ? {
              x: { display: false, grid: { display: false } },
              yLeft: { display: false, grid: { display: false } },
              yRight: { display: false, grid: { display: false } },
            }
          : {
              x: {
                display: false, // Hide x-axis labels in thumbnail
                grid: {
                  display: false,
                },
              },
              y: {
                display: false, // Hide y-axis labels in thumbnail
                grid: {
                  display: false,
                },
              },
            }
      ) : {},
    };

    if (type === 'dual_axis_line') {
      return <Line data={chartData} options={thumbnailOptions} />;
    } else if (type === 'line' || type === 'group_line') {
      return <Line data={chartData} options={thumbnailOptions} />;
    } else if (type === 'bar' || type === 'group_bar') {
      return <Bar data={chartData} options={thumbnailOptions} />;
    } else if (type === 'pie' || type === 'donut') {
      return <Pie data={chartData} options={thumbnailOptions} />;
    }
    
    return null;
  };

  // Render thumbnail table for card preview
  const renderThumbnailTable = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return null;
    }

    const keys = Object.keys(data[0]);
    const maxRows = Math.min(3, data.length); // Show max 3 rows in thumbnail
    const maxCols = Math.min(3, keys.length); // Show max 3 columns in thumbnail

    return (
      <div className="w-full h-full overflow-hidden">
        <table className="w-full text-[8px] text-gray-700">
          <thead>
            <tr className="border-b bg-gray-100">
              {keys.slice(0, maxCols).map((key, idx) => (
                <th key={idx} className="px-1 py-0.5 text-left font-semibold truncate max-w-[60px]">
                  {key.replace(/_/g, ' ').substring(0, 8)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.slice(0, maxRows).map((row, rowIdx) => (
              <tr key={rowIdx} className="border-b">
                {keys.slice(0, maxCols).map((key, keyIdx) => (
                  <td key={keyIdx} className="px-1 py-0.5 truncate max-w-[60px]">
                        {formatTableCellValue(row[key], key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const downloadCSVFromModal = () => {
    if (!reportData || !reportData.dataTable || !Array.isArray(reportData.dataTable) || reportData.dataTable.length === 0) {
      return;
    }

    const keys = Object.keys(reportData.dataTable[0]);
    const orderedHeaders = [];
    if (reportData.visualization?.x_axis && keys.includes(reportData.visualization.x_axis)) {
      orderedHeaders.push(reportData.visualization.x_axis);
    }
    keys.forEach(key => {
      if (!orderedHeaders.includes(key)) {
        orderedHeaders.push(key);
      }
    });
    
    const headerRow = orderedHeaders.map(header => 
      `"${header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}"`
    ).join(',');
    
    const dataRows = reportData.dataTable.map(row => {
      return orderedHeaders.map(header => {
        const value = row[header];
        const stringValue = value === null || value === undefined ? '' : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      }).join(',');
    });
    
    const csvContent = [headerRow, ...dataRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${reportData.csvFileName || 'report_' + new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const unpinReport = async (reportId) => {
    try {
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      await axios.delete(`${API_BASE_URL}/api/pinned-reports/${reportId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setToast({ message: 'Report unpinned successfully', type: 'success' });
      fetchPinnedReports(); // Refresh pinned reports list
    } catch (error) {
      if (error.isSessionExpired || (error.response && error.response.status === 401)) {
        return;
      }
      console.error('Error unpinning report:', error);
      setToast({ message: error.response?.data?.error || 'Failed to unpin report', type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading pinned reports...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="p-6">
        <PanelBreadcrumbs current="Pinned Reports" setActivePage={setActivePage} />
        <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Pinned Reports</h1>
        <p className="text-gray-600">View and manage your pinned reports</p>
      </div>

      {pinnedReports.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <div className="text-gray-400 text-6xl mb-4">📌</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Pinned Reports</h3>
          <p className="text-gray-500">Pin reports from the DB Agent Chat to view them here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pinnedReports.map((report) => {
            // Parse visualization and data for thumbnail
            let visualization = report.visualization;
            if (visualization && typeof visualization === 'string') {
              try {
                visualization = JSON.parse(visualization);
              } catch (e) {
                visualization = null;
              }
            }
            
            let dataTable = report.data || [];
            if (!Array.isArray(dataTable)) {
              dataTable = [];
            }

            const hasVisualization = visualization && visualization.type && visualization.type !== 'table';
            const hasData = dataTable.length > 0;

            return (
              <div
                key={report.id}
                className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow"
              >
                {/* Thumbnail Preview */}
                {(hasVisualization || hasData) && (
                  <div className="mb-3 h-32 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center">
                    {hasVisualization && visualization.type !== 'table' ? (
                      <div className="w-full h-full p-2">
                        {renderThumbnailChart(visualization, dataTable)}
                      </div>
                    ) : hasData ? (
                      <div className="w-full h-full p-2">
                        {renderThumbnailTable(dataTable)}
                      </div>
                    ) : (
                      <span className="text-2xl">{getVisualizationIcon(visualization?.type)}</span>
                    )}
                  </div>
                )}

                {/* Header with Title and Date */}
                <div className="mb-3">
                  <h3 className="font-semibold text-gray-900 text-base mb-1 truncate">
                    {report.report_title || 'Untitled Report'}
                  </h3>
                  {report.created_at && (
                    <p className="text-xs text-gray-500">
                      {formatDate(report.created_at)}
                    </p>
                  )}
                </div>

                {/* Description */}
                {report.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {report.description}
                  </p>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => viewPinnedReport(report)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                  >
                    View
                  </button>
                  {report.csv_file_name && (
                    <button
                      onClick={() => downloadCSV(report)}
                      className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Download CSV"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => unpinReport(report.id)}
                    className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    title="Remove Pin"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal for Viewing Report */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {selectedReport.report_title || 'Pinned Report'}
                </h2>
                {selectedReport.created_at && (
                  <p className="text-sm text-gray-500 mt-1">
                    {formatDate(selectedReport.created_at)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {/* Download CSV Button */}
                {reportData && reportData.dataTable && 
                 Array.isArray(reportData.dataTable) && 
                 reportData.dataTable.length > 0 && (
                  <button
                    onClick={downloadCSVFromModal}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                    title="Download as CSV"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download CSV
                  </button>
                )}
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingReport ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-gray-500">Loading report data...</div>
                </div>
              ) : reportData ? (
                <>
                  {/* Report Description/Message */}
                  {reportData.content && (
                    <div className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="text-sm text-gray-700">{reportData.content}</div>
                    </div>
                  )}

                  {/* Show message if no data */}
                  {(!reportData.dataTable || !Array.isArray(reportData.dataTable) || reportData.dataTable.length === 0) && (
                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-yellow-800 text-sm">No data available for this report.</p>
                    </div>
                  )}

                  {/* Chart */}
                  {reportData.visualization && 
                   typeof reportData.visualization === 'object' && 
                   Object.keys(reportData.visualization).length > 0 &&
                   reportData.visualization.type && 
                   reportData.visualization.type !== 'table' && 
                   reportData.dataTable && 
                   Array.isArray(reportData.dataTable) && 
                   reportData.dataTable.length > 0 && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-xl border">
                      <div className="h-96">
                        {renderChart(reportData.visualization, reportData.dataTable)}
                      </div>
                    </div>
                  )}

                  {/* Data Table */}
                  {reportData.dataTable && 
                   Array.isArray(reportData.dataTable) && 
                   reportData.dataTable.length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-xl border">
                      <div className="flex justify-between items-center mb-4">
                        <div className="font-medium text-base text-gray-800">Data Summary</div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs text-gray-700 min-w-[200px]">
                          <thead>
                            <tr className="border-b bg-gray-100">
                              {(() => {
                                const keys = Object.keys(reportData.dataTable[0]);
                                const orderedKeys = [];
                                
                                if (reportData.visualization?.x_axis && keys.includes(reportData.visualization.x_axis)) {
                                  orderedKeys.push(reportData.visualization.x_axis);
                                }
                                
                                keys.forEach(key => {
                                  if (!orderedKeys.includes(key)) {
                                    orderedKeys.push(key);
                                  }
                                });
                                
                                return orderedKeys.map((key, keyIdx) => (
                                  <th 
                                    key={keyIdx} 
                                    className="px-3 py-2 text-left font-semibold text-gray-700"
                                  >
                                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </th>
                                ));
                              })()}
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.dataTable.map((row, rowIdx) => {
                              const keys = Object.keys(reportData.dataTable[0]);
                              const orderedKeys = [];
                              
                              if (reportData.visualization?.x_axis && keys.includes(reportData.visualization.x_axis)) {
                                orderedKeys.push(reportData.visualization.x_axis);
                              }
                              
                              keys.forEach(key => {
                                if (!orderedKeys.includes(key)) {
                                  orderedKeys.push(key);
                                }
                              });
                              
                              return (
                                <tr key={rowIdx} className="border-b hover:bg-gray-50">
                                  {orderedKeys.map((key, keyIdx) => (
                                    <td key={keyIdx} className="px-3 py-2">
                                      {formatTableCellValue(row[key], key)}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-gray-500">No data available</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default PinnedReports;
